import { useState } from 'react';
import { PencilIcon } from 'lucide-react';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  CopyToClipboard,
} from '@sandtech-hub-monorepo/ui-kit';
import { useGeoPicker } from '@sandtech-hub-monorepo/ui-kit-site';
import { Gender } from '@sandtech-hub-monorepo/ehub-client';

import { ProfileEditDialog } from './ProfileEditDialog';

interface Props {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  title?: string | null;
  gender?: Gender | null;
  profilePhoto?: string | null;
  countryOfResidence?: string | null;
  countyOfResidence?: string | null;
  sandId?: string | null;
}

export const ProfileHeader = ({
  firstName,
  middleName,
  lastName,
  title,
  gender,
  profilePhoto,
  countryOfResidence,
  countyOfResidence,
  sandId,
}: Props) => {
  const { getCountryByCode, getStateByCodeAndCountry } = useGeoPicker();
  const [editOpen, setEditOpen] = useState(false);

  const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ');

  const countryLabel = countryOfResidence
    ? getCountryByCode(countryOfResidence)?.label
    : undefined;
  const stateLabel =
    countryOfResidence && countyOfResidence
      ? getStateByCodeAndCountry(countyOfResidence, countryOfResidence)?.label
      : undefined;
  // Figma renders location as "State, Country" (e.g. "New York, USA").
  const location = [stateLabel, countryLabel].filter(Boolean).join(', ');

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-sm border border-border-secondary bg-card p-6">
      <div
        role="button"
        tabIndex={0}
        aria-label="Edit profile photo"
        onClick={() => setEditOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            setEditOpen(true);
          }
        }}
        className="flex items-center justify-center w-24 h-24 rounded-full border-2 border-white bg-background shrink-0 cursor-pointer"
      >
        <Avatar className="h-full w-full overflow-hidden">
          {profilePhoto ? (
            <AvatarImage src={profilePhoto} className="object-cover" />
          ) : (
            <AvatarFallback className="aspect-square h-full w-full text-2xl">
              {`${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`}
            </AvatarFallback>
          )}
        </Avatar>
      </div>

      <div className="flex flex-col gap-1 flex-1">
        <h1 className="text-2xl sm:text-3xl font-semibold text-heading">
          {fullName}
        </h1>
        {title && <p className="text-md text-muted-foreground">{title}</p>}
        {location && (
          <p className="text-md text-muted-foreground">{location}</p>
        )}
        {sandId && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <span>User ID: {sandId}</span>
            <CopyToClipboard text={sandId} />
          </div>
        )}
      </div>

      <Button
        onClick={() => setEditOpen(true)}
        variant="outline"
        aria-label="Edit profile"
        className="rounded-lg h-10 p-2 self-start"
      >
        <PencilIcon height="1.2em" />
      </Button>

      <ProfileEditDialog
        open={editOpen}
        handleOpenChange={setEditOpen}
        defaultValues={{
          firstName: firstName || '',
          lastName: lastName || '',
          title: title || '',
          gender: gender || undefined,
        }}
        profilePhoto={profilePhoto}
      />
    </div>
  );
};
